/**
 * IFC Family Importer
 *
 * Glue between the wasm parser and the family registry: for every product in
 * a parsed .ifc file it bakes the mesh into the IndexedDB asset store and
 * registers a "static mesh" FamilySchema through the standard FamilyLoader
 * path. Importing the same file twice replaces the previous entries from that
 * source (keyed by the IFC GlobalId-less source label), mirroring BIM's
 * reload-family behaviour.
 */

import { familyManager } from '../../core/FamilyManager';
import { familyLoader } from '../../core/services';
import { FamilySchema, FamilyParameterDefinition } from '../../core/families/FamilySchema';
import { parseIfcFile, IfcProductRecord, normalizeIfcType } from './IfcParserService';
import { storeIfcAsset } from './IfcAssetStore';

/** Emitted after every successful registration batch so UI (Project Browser) refreshes. */
export const FAMILY_REGISTRY_EVENT = 'yap:family-registry-changed';

export interface IfcImportSummary {
    fileName: string;
    families: string[]; // registered family names
    productCount: number;
}

export function notifyFamilyRegistryChanged(): void {
    window.dispatchEvent(new CustomEvent(FAMILY_REGISTRY_EVENT));
}

/** Pset properties → family instance parameters (values kept verbatim, shown in Properties). */
function psetToParameters(product: IfcProductRecord): FamilyParameterDefinition[] {
    const seen = new Set<string>();
    const params: FamilyParameterDefinition[] = [];
    for (const prop of product.properties) {
        if (seen.has(prop.name)) continue;
        seen.add(prop.name);
        params.push({
            name: prop.name,
            type: 'String',
            defaultValue: prop.value,
            isInstance: true,
            group: prop.set,
        });
    }
    return params;
}

/**
 * Import an .ifc file (raw bytes) as one static-mesh family per product.
 * Throws when the engine cannot open the model.
 */
export async function importIfcFile(fileName: string, bytes: Uint8Array): Promise<IfcImportSummary> {
    const parsed = await parseIfcFile(fileName, bytes);
    const registered: string[] = [];

    for (const product of parsed.products) {
        const assetId = await storeIfcAsset(product.name, `${fileName} #${product.expressID}`, product.parts, product.bbox);

        const familyName = `${product.name} (${normalizeIfcType(product.ifcType)})`;
        const schema: FamilySchema = {
            version: '1.0.0',
            name: familyName,
            category: product.category,
            parameters: psetToParameters(product),
            constraints: [],
            geometry: [], // no parametric instructions — static asset carries the shape
            types: [{ name: 'Default', values: {} }],
            staticAsset: {
                kind: 'ifc',
                assetId,
                units: 'm',
                source: `${fileName} #${product.expressID}`,
            },
        };

        // Reload semantics: registerSchema overwrites the same-name entry.
        await familyLoader.loadFamily(schema);
        registered.push(familyName);
    }

    if (registered.length > 0) {
        notifyFamilyRegistryChanged();
    }
    return { fileName, families: registered, productCount: parsed.products.length };
}

/** True when the family carries a baked mesh source. */
export function isStaticFamily(name: string): boolean {
    const family = familyManager.getFamily(name);
    return !!family?.geometrySchema?.staticAsset;
}
